import {Injectable} from '@angular/core';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {
  LoadSubmissionAdditionalData,
  LoadSubmissionAdditionalDataFail,
  LoadSubmissionAdditionalDataSuccess,
  ManageSubmissionAdditionalData,
  ManageSubmissionAdditionalDataFail,
  ManageSubmissionAdditionalDataSuccess, ResetDashboardSupplementalHealthManageLoadingIndicators,
  ResetDashboardSupplementalHealthState,
} from './dashboard-supplemental-health.actions';
import {
  dashboardSupplementalHealthStateDefaults,
  dashboardSupplementalHealthStateUniqueIdentifier,
  DashboardSupplementalHealthStateModel,
} from './dashboard-supplemental-health.model';
import {DashboardSupplementalHealthService} from '../../services/dashboard-supplemental-health/dashboard-supplemental-health.service';
import {PROGRESS_STATUSES} from '../../../shared/symbols';
import {ErrorMessage} from '../../../messages/messages.actions';

@State<DashboardSupplementalHealthStateModel>({
  name: dashboardSupplementalHealthStateUniqueIdentifier,
  defaults: dashboardSupplementalHealthStateDefaults,
})
@Injectable()
export class DashboardSupplementalHealthState {
  constructor(private clinicPatientsSingleSubmission: DashboardSupplementalHealthService) {}

  @Selector()
  static loadSubmissionAdditionalDataStatus(
    state: DashboardSupplementalHealthStateModel
  ): DashboardSupplementalHealthStateModel['loadSubmissionAdditionalDataStatus'] {
    return state.loadSubmissionAdditionalDataStatus;
  }

  @Selector()
  static lastLoadSubmissionAdditionalDataError(
    state: DashboardSupplementalHealthStateModel
  ): DashboardSupplementalHealthStateModel['lastLoadSubmissionAdditionalDataError'] {
    return state.lastLoadSubmissionAdditionalDataError;
  }

  @Selector()
  static manageSubmissionAdditionalDataStatus(
    state: DashboardSupplementalHealthStateModel
  ): DashboardSupplementalHealthStateModel['manageSubmissionAdditionalDataStatus'] {
    return state.manageSubmissionAdditionalDataStatus;
  }

  @Selector()
  static lastManageSubmissionAdditionalDataError(
    state: DashboardSupplementalHealthStateModel
  ): DashboardSupplementalHealthStateModel['lastManageSubmissionAdditionalDataError'] {
    return state.lastManageSubmissionAdditionalDataError;
  }

  @Selector()
  static submissionAdditionalData(
    state: DashboardSupplementalHealthStateModel
  ): DashboardSupplementalHealthStateModel['submissionAdditionalData'] {
    return state.submissionAdditionalData;
  }

  @Selector()
  static storedSubmissionSeriesUid(
    state: DashboardSupplementalHealthStateModel
  ): DashboardSupplementalHealthStateModel['storedSubmissionSeriesUid'] {
    return state.storedSubmissionSeriesUid;
  }

  @Action(LoadSubmissionAdditionalData)
  loadSubmissionAdditionalData(
    ctx: StateContext<DashboardSupplementalHealthStateModel>,
    {submissionId}: LoadSubmissionAdditionalData
  ): void {
    ctx.patchState({
      loadSubmissionAdditionalDataStatus: PROGRESS_STATUSES.IN_PROGRESS,
      lastLoadSubmissionAdditionalDataError: null,
      storedSubmissionSeriesUid: submissionId,
    });

    this.clinicPatientsSingleSubmission.loadSubmissionAdditionalData(submissionId).subscribe({
      next: SubmissionAdditionalDataBackendModel =>
        ctx.dispatch(new LoadSubmissionAdditionalDataSuccess(SubmissionAdditionalDataBackendModel)),
      error: error => ctx.dispatch(new LoadSubmissionAdditionalDataFail(error)),
    });
  }

  @Action(LoadSubmissionAdditionalDataSuccess)
  loadSubmissionAdditionalDataSuccess(
    ctx: StateContext<DashboardSupplementalHealthStateModel>,
    {submissionAdditionalData}: LoadSubmissionAdditionalDataSuccess
  ): void {
    ctx.patchState({
      loadSubmissionAdditionalDataStatus: PROGRESS_STATUSES.SUCCEED,
      submissionAdditionalData,
    });
  }

  @Action(LoadSubmissionAdditionalDataFail)
  loadSubmissionAdditionalDataFail(
    ctx: StateContext<DashboardSupplementalHealthStateModel>,
    {error}: LoadSubmissionAdditionalDataFail
  ): void {
    ctx.patchState({
      loadSubmissionAdditionalDataStatus: PROGRESS_STATUSES.INTERRUPTED,
      lastLoadSubmissionAdditionalDataError: error,
    });

    // Dispatch an error message to the user.
    ctx.dispatch(new ErrorMessage(error.message));
  }

  @Action(ManageSubmissionAdditionalData)
  manageSubmissionAdditionalData(
    ctx: StateContext<DashboardSupplementalHealthStateModel>,
    {submissionId, submissionAdditionalData, isUpdate}: ManageSubmissionAdditionalData
  ): void {
    ctx.patchState({
      manageSubmissionAdditionalDataStatus: PROGRESS_STATUSES.IN_PROGRESS,
      lastManageSubmissionAdditionalDataError: null,
      storedSubmissionSeriesUid: submissionId,
    });

    this.clinicPatientsSingleSubmission.manageSubmissionAdditionalData(submissionId, submissionAdditionalData, isUpdate).subscribe({
      next: submissionAdditionalDataBackendModel =>
        ctx.dispatch(new ManageSubmissionAdditionalDataSuccess(submissionAdditionalDataBackendModel)),
      error: error => ctx.dispatch(new ManageSubmissionAdditionalDataFail(error)),
    });
  }

  @Action(ManageSubmissionAdditionalDataSuccess)
  manageSubmissionAdditionalDataSuccess(
    ctx: StateContext<DashboardSupplementalHealthStateModel>,
    {submissionAdditionalData}: ManageSubmissionAdditionalDataSuccess
  ): void {
    ctx.patchState({
      manageSubmissionAdditionalDataStatus: PROGRESS_STATUSES.SUCCEED,
      submissionAdditionalData,
    });
  }

  @Action(ManageSubmissionAdditionalDataFail)
  manageSubmissionAdditionalDataFail(
    ctx: StateContext<DashboardSupplementalHealthStateModel>,
    {error}: ManageSubmissionAdditionalDataFail
  ): void {
    ctx.patchState({
      manageSubmissionAdditionalDataStatus: PROGRESS_STATUSES.INTERRUPTED,
      lastManageSubmissionAdditionalDataError: error,
    });

    // Dispatch an error message to the user.
    ctx.dispatch(new ErrorMessage(error.message));
  }

  @Action(ResetDashboardSupplementalHealthManageLoadingIndicators)
  resetDashboardSupplementalHealthManageLoadingIndicators({patchState}: StateContext<DashboardSupplementalHealthStateModel>): void {
    patchState({
      manageSubmissionAdditionalDataStatus: PROGRESS_STATUSES.NOT_INITIALIZED,
      lastManageSubmissionAdditionalDataError: null,
    });
  }

  @Action(ResetDashboardSupplementalHealthState)
  resetClinicPatientsSingleSubmissionState({patchState}: StateContext<DashboardSupplementalHealthStateModel>): void {
    patchState({...dashboardSupplementalHealthStateDefaults});
  }
}
